import { useQuery } from '@tanstack/react-query'
import { useSettingsStore } from '../stores/settingsStore'
import { useMarketStore } from '../services/store'
import { useEffect, useMemo, useState } from 'react'
import { fetchHistoricalCandles, type UpstoxCandle } from '../services/candle-service'
import { IndexSymbol, FUTURES_FEED_CONFIG, type FutureQuoteData } from '../config/constants'

export function useCurrentFuture(symbol: string = 'NIFTY', spotPrice: number = 0, activeTimestamp?: number) {
    const isLive = useSettingsStore(s => s.isLive)
    const historicalDate = useSettingsStore(s => s.historicalDate)
    const inspectionSource = useSettingsStore(s => s.inspectionSource)

    // Normalize symbol
    const norm = (symbol || 'NIFTY').toUpperCase().replace(/\s+/g, '')
    let symbolKey: IndexSymbol = IndexSymbol.NIFTY
    if (norm.includes('BANK')) symbolKey = IndexSymbol.BANKNIFTY
    else if (norm.includes('SENSEX')) symbolKey = IndexSymbol.SENSEX

    // 1. LIVE FETCHING: Query /api/future-quote every 2 seconds
    const { data: liveData, isLoading: isLiveLoading } = useQuery<{ success: boolean; data: FutureQuoteData | null }>({
        queryKey: ['current-future-live', symbolKey],
        queryFn: async () => {
            try {
                const res = await fetch(`${FUTURES_FEED_CONFIG.PROXY_URL}?symbol=${symbolKey}`)
                if (!res.ok) return { success: false, data: null }
                return await res.json()
            } catch {
                return { success: false, data: null }
            }
        },
        enabled: isLive && inspectionSource !== 'HistoricalSnapshot',
        refetchInterval: FUTURES_FEED_CONFIG.REFRESH_MS, // 2000ms
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: false,
        retry: 1,
        staleTime: 1500,
    })

    // 2. HISTORICAL CANDLE FETCHING
    const token = FUTURES_FEED_CONFIG.UPSTOX_FUT_TOKENS[symbolKey] || 'NSE_FO|68407'
    const { data: historicalCandles, isLoading: isHistLoading } = useQuery({
        queryKey: ['future-history', symbolKey, historicalDate],
        queryFn: async () => {
            if (!historicalDate) return []
            const candles = await fetchHistoricalCandles(token, 1, '1minute', token, historicalDate)
            return candles
        },
        enabled: !isLive && inspectionSource === 'HistoricalSnapshot' && !!historicalDate,
        staleTime: Infinity,
    })

    // 3. HISTORICAL TIMESTAMP SYNC
    const [historicalLtp, setHistoricalLtp] = useState<number>(0)
    const effectiveTimestamp = activeTimestamp || Date.now()

    useEffect(() => {
        if (!isLive && inspectionSource === 'HistoricalSnapshot' && historicalCandles && historicalCandles.length > 0 && effectiveTimestamp > 0) {
            let closestCandle: UpstoxCandle | null = null
            let minDiff = Infinity

            for (const candle of historicalCandles) {
                const cTime = new Date(candle.timestamp).getTime()
                const diff = Math.abs(cTime - effectiveTimestamp)
                if (diff < minDiff) {
                    minDiff = diff
                    closestCandle = candle
                }
            }

            if (closestCandle) {
                setHistoricalLtp(closestCandle.close)
            }
        }
    }, [isLive, inspectionSource, historicalCandles, effectiveTimestamp])

    // 4. EMERGENCY SAFETY NET: Put-Call Parity Implied Future F = K_ATM + (C_ATM - P_ATM)
    // Read live option chain from market store to calculate synthetic future if needed
    const optionRows = useMarketStore(s => s.data?.[symbolKey])
    const parityFuture = useMemo(() => {
        if (!spotPrice || spotPrice <= 0 || !optionRows || optionRows.length === 0) return 0

        // Find ATM strike (closest to spot)
        let atmStrike = optionRows[0]?.strike || 0
        let minDiff = Math.abs((optionRows[0]?.strike || 0) - spotPrice)
        let atmRow = optionRows[0]

        for (const row of optionRows) {
            const diff = Math.abs(row.strike - spotPrice)
            if (diff < minDiff) {
                minDiff = diff
                atmStrike = row.strike
                atmRow = row
            }
        }

        if (atmRow && (atmRow.ce?.ltp || 0) > 0 && (atmRow.pe?.ltp || 0) > 0) {
            return atmStrike + ((atmRow.ce?.ltp || 0) - (atmRow.pe?.ltp || 0))
        }
        return 0
    }, [spotPrice, optionRows, symbolKey])

    // 5. DETERMINE ACTIVE FUTURE VALUES
    const liveQuote = liveData?.data
    const isHistoricalMode = !isLive && inspectionSource === 'HistoricalSnapshot'

    let activeLtp = 0
    let activeClose = 0
    let activeDayChange = 0
    let activeDayChangePct = 0
    let source: 'GROWW' | 'UPSTOX' | 'BSE_INDIA' | 'PARITY' = 'GROWW'

    if (isHistoricalMode) {
        activeLtp = historicalLtp
        activeClose = historicalCandles?.[0]?.open || historicalLtp
        activeDayChange = activeClose > 0 ? (activeLtp - activeClose) : 0
        activeDayChangePct = activeClose > 0 ? (activeDayChange / activeClose) * 100 : 0
        source = 'UPSTOX'
    } else if (liveQuote && liveQuote.futureLtp > 0) {
        activeLtp = liveQuote.futureLtp
        activeClose = liveQuote.closePrice
        activeDayChange = liveQuote.dayChange
        activeDayChangePct = liveQuote.dayChangePct
        source = liveQuote.source
    } else if (parityFuture > 0) {
        // Fallback to Parity
        activeLtp = parityFuture
        activeClose = spotPrice
        activeDayChange = activeLtp - spotPrice
        activeDayChangePct = spotPrice > 0 ? (activeDayChange / spotPrice) * 100 : 0
        source = 'PARITY'
    }

    // 6. SPREAD / BASIS CALCULATIONS
    // Basis = Future LTP - Spot LTP
    // Premium if Future >= Spot, Discount if Future < Spot
    const basis = (activeLtp > 0 && spotPrice > 0) ? (activeLtp - spotPrice) : 0
    const basisPct = (activeLtp > 0 && spotPrice > 0) ? ((activeLtp - spotPrice) / spotPrice) * 100 : 0
    const isPremium = activeLtp >= spotPrice

    return {
        futureLtp: activeLtp,
        closePrice: activeClose,
        dayChange: activeDayChange,
        dayChangePct: activeDayChangePct,
        basis,
        basisPct,
        isPremium,
        contractName: liveQuote?.contractName || `${symbolKey} FUT`,
        expiry: liveQuote?.expiry,
        lotSize: liveQuote?.lotSize,
        source,
        isLoading: isHistoricalMode ? isHistLoading : (isLiveLoading && activeLtp === 0),
    }
}
